import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Download, BookOpen, CheckCircle, Target, TrendingUp, DollarSign, Heart, Shield, ArrowRight, Play, Calendar, Users, Star, Linkedin } from 'lucide-react';
import SpotlightCard from '../components/reactbits/SpotlightCard';

const BookLanding = () => {
  const navigate = useNavigate();

  const handleOrderClick = () => {
    navigate('/kniha/objednavka');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="book-landing font-body text-brand-dark-blue overflow-x-hidden">

      {/* 1. HERO SECTION - HIERARCHY & PRODUCT */}
      <section className="relative bg-brand-dark-blue text-white min-h-[90vh] flex items-center overflow-x-clip py-20 lg:py-0">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '400px', backgroundRepeat: 'repeat' }} />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-orange/20 rounded-full blur-[120px]" />

        <div className="container mx-auto px-4 sm:px-5 relative z-10 max-w-full min-w-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center min-w-0">
            <div className="order-2 lg:order-1 max-w-xl min-w-0">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-brand-orange text-sm font-bold uppercase tracking-wide mb-8 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse"></span>
                Novinka 2026
              </div>

              <h1 className="text-3xl min-[400px]:text-4xl sm:text-5xl lg:text-7xl font-heading font-bold mb-6 leading-[1.1] break-words">
                Renta bez <span className="text-brand-orange">dátumu spotreby</span>
              </h1>

              <p className="text-base sm:text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed font-light">
                Moja prvá kniha. Kompletný systém budovania majetku, overený 20 rokmi praxe.
                <br /><span className="text-gray-400 text-lg mt-2 block">Prestaňte sa báť o budúcnosť a začnite budovať systém.</span>
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <button
                  onClick={handleOrderClick}
                  className="group inline-flex items-center justify-center bg-brand-orange hover:bg-red-600 text-white font-bold py-5 px-8 rounded-xl transition-all duration-300 shadow-xl hover:shadow-orange-500/20 text-lg"
                >
                  <Download className="mr-3 w-6 h-6 group-hover:-translate-y-1 transition-transform" />
                  Stiahnuť knihu zdarma
                </button>

                <button
                  onClick={handleOrderClick}
                  className="inline-flex items-center justify-center bg-transparent border-2 border-gray-600 text-gray-300 font-semibold py-5 px-8 rounded-xl hover:bg-white/5 hover:text-white hover:border-white transition-all text-lg"
                >
                  <BookOpen className="mr-3 w-6 h-6" />
                  Predbežný záujem o fyzickú knihu
                </button>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-400">
                <span className="flex items-center gap-2"><CheckCircle size={16} className="text-green-500" /> Praktický manuál</span>
                <span className="flex items-center gap-2"><CheckCircle size={16} className="text-green-500" /> Prečítate za jeden večer</span>
              </div>
            </div>

            <div className="order-1 lg:order-2 flex justify-center lg:justify-end relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-brand-orange/10 to-transparent rounded-full blur-3xl" />
              <div className="relative transform transition-transform hover:scale-[1.02] duration-500">
                <img src="/kniha_cover.jpeg" alt="Kniha Renta" className="relative z-10 w-full max-w-sm lg:max-w-md h-auto rounded-xl shadow-2xl border border-white/10" />
                <div className="absolute -bottom-6 -right-4 bg-white text-brand-dark-blue p-4 rounded-xl shadow-xl z-20 hidden md:block animate-fade-in-up">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Hodnotenie</p>
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-bold">4.9</span>
                    <div className="flex text-yellow-500 text-sm">★★★★★</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. CONTEXT & MISSION - WITH PHOTO */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div className="relative">
              <div className="absolute -inset-4 bg-gray-100 rounded-3xl transform -rotate-3" />
              <img src="/pribeh_s3.jpeg" alt="Peto Sloboda pri práci" className="relative rounded-2xl shadow-lg w-full h-auto object-cover transform rotate-0 hover:rotate-1 transition-transform duration-500" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-5xl font-heading font-bold mb-6">
                Prečo dávam svoje 20-ročné know-how <span className="text-brand-orange underline decoration-brand-orange/30 underline-offset-4">zadarmo?</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-6">
                Nie je v tom háčik. Môj cieľ je vzdelať trh. Chcem, aby čo najviac ľudí na Slovensku pochopilo, že budovanie majetku nie je lotéria, ale systém.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Ak vám táto kniha pomôže, možno sa v budúcnosti stretneme ako partneri pri správe vášho majetku. Ale prvý krok je na vás – vzdelanie.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-brand-dark-blue font-bold text-sm">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-gray-400 uppercase text-[10px] mb-1">E-book (PDF)</p>
                  <p>ISBN 978-80-570-7714-5</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-gray-400 uppercase text-[10px] mb-1">Tlačená kniha (Brožovaná)</p>
                  <p>ISBN 978-80-570-7713-8</p>
                </div>
              </div>

              <div className="mt-10 text-center lg:text-left">
                <button
                  onClick={handleOrderClick}
                  className="group inline-flex items-center justify-center bg-brand-orange hover:bg-red-600 text-white font-bold py-5 px-10 rounded-2xl transition-all duration-300 shadow-xl hover:shadow-orange-500/20 text-lg"
                >
                  <Download className="mr-3 w-6 h-6 group-hover:-translate-y-1 transition-transform" />
                  Stiahnuť knihu zdarma
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. AUDIENCE - WITH VISUALS */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-brand-dark-blue">
              Pre koho som knihu napísal?
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4 translate-y-8">
                <img src="/book_4.jpeg" alt="" className="rounded-2xl shadow-lg w-full h-auto object-cover" />
                <img src="/book_6.jpeg" alt="" className="rounded-2xl shadow-lg w-full h-auto object-cover" />
              </div>
              <div className="space-y-4">
                <img src="/book_5.jpeg" alt="" className="rounded-2xl shadow-lg w-full h-auto object-cover" />
                <img src="/book_7.jpeg" alt="" className="rounded-2xl shadow-lg w-full h-auto object-cover" />
              </div>
            </div>

            <div className="space-y-4">
              {[
                { title: 'Podnikatelia', desc: 'Ktorí chcú oddeliť firemné a súkromné peniaze.', icon: Target },
                { title: 'Manažéri', desc: 'Ktorí hľadajú cestu von z "krysieho závodu".', icon: TrendingUp },
                { title: 'Rodiny', desc: 'Ktoré chcú zabezpečiť majetok pre ďalšie generácie.', icon: Shield },
                { title: 'Investori', desc: 'Ktorí chcú systém a nie len náhodné tipy.', icon: DollarSign }
              ].map((item, i) => (
                <div key={i} className="flex items-center p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-brand-dark-blue mr-5 flex-shrink-0">
                    <item.icon size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-brand-dark-blue">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. PREVIEW SECTION - VIAC NEZ LEN TEORIA (UX Priority: Sell content first) */}
      <section className="section bg-brand-dark-blue text-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div className="order-2 lg:order-1">
              <div className="inline-block px-3 py-1 rounded bg-brand-orange text-white text-xs font-bold uppercase mb-4">
                Nahliadnite dovnútra
              </div>
              <h2 className="text-4xl font-heading font-bold mb-6">
                Viac než len teória
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                Pozrite si úvodné video. Vysvetľujem v ňom, prečo je táto kniha iná. Nie sú to len stránky textu, je to komplexný manuál s pracovnými listami, qr kódmi a schémami.
              </p>
              <button onClick={handleOrderClick} className="bg-white text-brand-dark-blue font-bold px-8 py-4 rounded-xl hover:bg-gray-100 transition-colors flex items-center gap-2">
                <Download size={20} />
                Chcem kompletný obsah
              </button>
            </div>
            <div className="order-1 lg:order-2 relative aspect-video rounded-3xl overflow-hidden shadow-2xl bg-black border border-white/20">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/A9TOOza0f7E?si=zNZWkr3CM9CtRQVr"
                title="YouTube video player"
                frameBorder="0"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BONUS VIDEO GALLERY (Value Add) */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-heading font-bold mb-4 text-brand-dark-blue">
              Video tréning ku knihe
            </h2>
            <p className="text-gray-600">Súčasťou knihy je aj séria videí, ktoré rozvíjajú kľúčové myšlienky.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { title: 'Prečo ľudia nemajú rentu', src: 'https://www.youtube.com/embed/MKtgpKUkHSs?si=eKZtu_ikcSSBedtj' },
              { title: 'Mindset "normálne bohatého"', src: 'https://www.youtube.com/embed/KWE4dRkBCSA?si=v6J8VC7t3bFwjXYE' },
              { title: 'Renta ako systém', src: 'https://www.youtube.com/embed/hqdWIt69yKc?si=LngKLkGkssTMW6w1' },
            ].map((video, i) => (
              <div key={i} className="group bg-gray-50 p-4 rounded-3xl transition-all hover:bg-white hover:shadow-xl border border-gray-100">
                <div className="relative aspect-video bg-gray-900 rounded-2xl overflow-hidden mb-4 shadow-sm group-hover:shadow-md">
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={video.src}
                    title={video.title}
                    frameBorder="0"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <h3 className="font-heading font-bold text-lg text-brand-dark-blue px-2">{video.title}</h3>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button onClick={handleOrderClick} className="inline-flex items-center text-brand-orange font-bold hover:text-red-500 transition-colors">
              Získať prístup ku všetkým bonusom <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* 6. SOCIAL PROOF (REVIEWS) */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-heading font-bold text-center mb-16 text-brand-dark-blue">
            Čo hovoria čitatelia?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-10 rounded-3xl relative">
              <div className="flex text-yellow-500 mb-4">★★★★★</div>
              <p className="text-lg text-gray-700 italic mb-6 leading-relaxed">
                „Je to výborne napísané. Prehľadné a jasné pre bežného smrteľníka… Sám by som nevedel lepšie napísať jednoduchšou a presvedčivejšou formou pre svoju rodinu a najmä deti.“
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-dark-blue text-white rounded-full flex items-center justify-center font-bold text-xl">R</div>
                <div>
                  <p className="font-bold text-brand-dark-blue">Rudi</p>
                  <p className="text-sm text-gray-500">Investor</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-10 rounded-3xl relative">
              <div className="flex text-yellow-500 mb-4">★★★★★</div>
              <p className="text-lg text-gray-700 italic mb-6 leading-relaxed">
                „Zrozumiteľne napísaná kniha, akurát som ju dočítala a vôbec som sa nenudila a ani som nemala pocit, že poučuje. Je to návod na život.“
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-orange text-white rounded-full flex items-center justify-center font-bold text-xl">J</div>
                <div>
                  <p className="font-bold text-brand-dark-blue">Jarka</p>
                  <p className="text-sm text-gray-500">Čitateľka</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-10 rounded-3xl relative">
              <div className="flex text-yellow-500 mb-4">★★★★★</div>
              <p className="text-lg text-gray-700 italic mb-6 leading-relaxed">
                „Táto kniha je povinná jazda pre každého, kto chce prevziať zodpovednosť za svoju finančnú budúcnosť. Páči sa mi, ako je kniha písaná zrozumiteľne, jednoducho a hlavne obohatená o praktické cvičenia. Pri jej čítaní som si hneď pripravila vlastný systematický plán.“
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-baby-blue rounded-full flex items-center justify-center overflow-hidden">
                  <img src="/ref_4.jpg" alt="Romana Bodnárová" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-bold text-brand-dark-blue">Romana Bodnárová</p>
                  <p className="text-sm text-gray-500">Čitateľka</p>
                </div>
                <a
                  href="https://sk.linkedin.com/in/romana-bodn%C3%A1rov%C3%A1"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn – Romana Bodnárová"
                  className="ml-auto text-gray-400 hover:text-brand-orange transition-colors"
                >
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. AUTHOR STORYTELLING - WITH LARGE PHOTO */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center max-w-6xl mx-auto">
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl skew-y-1">
                <img src="/pribeh_hero.jpeg" alt="Peťo Sloboda" className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="inline-block px-3 py-1 bg-white border border-gray-200 rounded-full text-xs font-bold uppercase tracking-wider mb-4 text-brand-dark-blue">
                O autorovi
              </div>
              <h2 className="text-4xl font-heading font-bold mb-6 text-brand-dark-blue">
                Od hokeja k rentiérstvu
              </h2>
              <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
                <p>
                  Moja cesta nebola priamočiara. Začal som na ľade ako <strong>hokejista</strong>. Tam som sa naučil disciplíne a tomu, že talent bez systému nič neznamená.
                </p>
                <p>
                  Tieto princípy som neskôr preniesol do <strong>podnikania</strong>. Zažil som vzostupy aj pády, kým som pochopil, že zarábať peniaze a mať peniaze, ktoré pracujú pre vás, sú dve rozdielne disciplíny.
                </p>
                <p>
                  Posledných 20 rokov pôsobím ako <strong>sprievodca svetom financií</strong>. Nie som teoretik z banky. Všetko, čo v knihe píšem, som si odžil na vlastnej koži.
                </p>
              </div>
              <div className="mt-8 flex gap-4">
                <button onClick={handleOrderClick} className="text-brand-orange font-bold hover:underline">
                  Získať knihu
                </button>
                <a href="/o-mne" className="text-brand-dark-blue font-bold hover:underline ml-6">
                  Celý môj príbeh →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. COMBINED DUAL CTA - PREMIUM REDESIGN */}
      <section className="py-24 bg-brand-dark-blue text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '300px', backgroundRepeat: 'repeat' }} />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">

            {/* Left: Book CTA */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-10 lg:p-14 rounded-[3rem] flex flex-col items-center text-center group hover:bg-white/10 transition-all duration-500">
              <div className="w-20 h-20 bg-brand-orange/20 rounded-2xl flex items-center justify-center text-brand-orange mb-8 group-hover:scale-110 transition-transform">
                <Download size={40} />
              </div>
              <h2 className="text-4xl font-heading font-bold mb-6 leading-tight">
                Začnite čítať <br /> <span className="text-brand-orange">ešte dnes</span>
              </h2>
              <p className="text-xl text-gray-300 mb-10 max-w-md">
                Prvý krok k rente je pochopiť jej pravidlá. Kniha je váš ťahák v digitálnej aj fyzickej forme.
              </p>
              <button
                onClick={handleOrderClick}
                className="w-full bg-brand-orange hover:bg-red-600 text-white font-bold py-5 rounded-2xl shadow-xl transition-all text-xl flex items-center justify-center gap-2 transform hover:scale-[1.02]"
              >
                <Download size={24} />
                Stiahnuť zdarma
              </button>
              <p className="mt-6 text-sm text-gray-400">
                PDF ihneď • Fyzická kniha softcover
              </p>
            </div>

            {/* Right: Consultation CTA */}
            <div className="bg-white p-10 lg:p-14 rounded-[3rem] flex flex-col items-center text-center group hover:shadow-2xl transition-all duration-500">
              <div className="w-20 h-20 bg-brand-dark-blue/5 rounded-2xl flex items-center justify-center text-brand-dark-blue mb-8 group-hover:scale-110 transition-transform">
                <Users size={40} />
              </div>
              <h2 className="text-4xl font-heading font-bold text-brand-dark-blue mb-6 leading-tight">
                Hľadáte <br /> <span className="text-gray-400">individuálnu cestu?</span>
              </h2>
              <p className="text-xl text-gray-600 mb-10 max-w-md">
                Ak hľadáte pomoc s implementáciou systému do vašej situácie (firma, rodina), som tu pre vás.
              </p>
              <Link
                to="/konzultace"
                className="w-full border-2 border-brand-dark-blue text-brand-dark-blue hover:bg-brand-dark-blue hover:text-white font-bold py-5 rounded-2xl transition-all text-xl flex items-center justify-center gap-2 transform hover:scale-[1.02]"
              >
                <Calendar size={24} />
                Nezáväzná konzultácia
              </Link>
              <p className="mt-6 text-sm text-gray-400">
                15-minútový zoznamovací call
              </p>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default BookLanding;
